import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { getClientById, editClient, applyServicePricing } from '../services/clientService';
import { uploadFiles, deleteFile, getSignedUrl } from '../services/fileService';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { terminology } from '../config/terminology'; // Import terminology
import { FaFileUpload, FaPaperclip, FaTimes, FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaTag, FaSync } from 'react-icons/fa';
import ServicePricingModal from '../components/ServicePricingModal';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ActivityLog from '../components/ActivityLog';

const EditClientPage = () => {
  const { id: clientId } = useParams(); // Get client ID from URL
  const navigate = useNavigate();
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createdAt, setCreatedAt] = useState(null); // For the activity log's synthetic "created" row
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [initialUploadedFilesCount, setInitialUploadedFilesCount] = useState(0); // Track initial count
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, file: null });
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // Service Pricing state
  const [servicePricing, setServicePricing] = useState({});
  const [clientName, setClientName] = useState('');
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [applyingPricing, setApplyingPricing] = useState(false);
  const [applyResult, setApplyResult] = useState(null); // { schedulesUpdated, jobsUpdated, skipped }
  const [applyError, setApplyError] = useState(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  const { register, handleSubmit, reset, setValue, control, getValues, formState: { errors, isSubmitting, isDirty } } = useForm({
    mode: 'onBlur',
    defaultValues: {
        clientName: '',
        email: '',
        phoneNumber: '',
        firstName: '',
        lastName: '',
        caseManagerFirstName: '',
        caseManagerLastName: '',
        caseManagerEmail: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        postalCode: '',
        country: 'Australia', // Default and read-only
        xeroContactId: '', // Added xeroContactId
        poNumber: '',
        newPoNumber: '', // Effective-dated PO replacement
        poEffectiveDate: '', // 'YYYY-MM-DD', services completed from this date use newPoNumber
        agedCare: false, // Aged Care checkbox
        hourlyRate: '', // Hourly rate
        durationInIncrements: false, // Duration in 15 min increments
    }
  });

  useEffect(() => {
    const fetchClient = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const data = await getClientById(clientId);
        setCreatedAt(data.createdAt || null);
        // Map fetched data to form fields, ensuring optional fields are empty strings if null/undefined
        const formData = {
          clientName: data.clientName || '',
          email: data.email || '',
          phoneNumber: data.phoneNumber || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          caseManagerFirstName: data.caseManagerFirstName || '',
          caseManagerLastName: data.caseManagerLastName || '',
          caseManagerEmail: data.caseManagerEmail || '',
          addressLine1: data.addressLine1 || '',
          addressLine2: data.addressLine2 || '',
          state: data.state || '',
          postalCode: data.postalCode || '',
          country: data.country || 'Australia', // Ensure country is set, default to Australia
          xeroContactId: data.xeroContactId || '', // Added xeroContactId
          poNumber: data.poNumber || '',
          newPoNumber: data.newPoNumber || '',
          poEffectiveDate: data.poEffectiveDate || '',
          agedCare: data.agedCare || false, // Aged Care checkbox
          hourlyRate: data.hourlyRate || '', // Hourly rate
          durationInIncrements: data.durationInIncrements || false, // Duration in 15 min increments
        };
        reset(formData); // Populate form with fetched data
        setClientName(data.clientName || '');
        setServicePricing(data.servicePricing || {});
        
        // Handle existing attachments from the API response
        if (data.attachments && Array.isArray(data.attachments)) {
          const existingFiles = data.attachments.map((attachment, index) => ({
            id: `existing-${index}-${Date.now()}`, // Unique ID for existing files
            name: extractFileNameFromUrl(attachment.url) || `File ${index + 1}`,
            category: attachment.type || 'Unknown',
            url: attachment.url,
            size: 0, // Size unknown for existing files
            uploaded: true,
            existing: true // Mark as existing file
          }));
          setUploadedFiles(existingFiles);
          setInitialUploadedFilesCount(existingFiles.length);
        } else {
          setUploadedFiles([]);
          setInitialUploadedFilesCount(0);
        }
      } catch (error) {
        setApiError(error.message || `Failed to load ${terminology.client.singular.toLowerCase()} details.`); // Use terminology
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [clientId, reset]);

  // File handling functions
  const extractFileNameFromUrl = (url) => {
    if (!url) return null;
    try {
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      // Remove query parameters if any
      const cleanFileName = fileName.split('?')[0] || 'Unknown File';
      // Decode URL encoding (e.g., %20 becomes space)
      return decodeURIComponent(cleanFileName);
    } catch (error) {
      return 'Unknown File';
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <FaFilePdf className="text-red-500" />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': return <FaFileImage className="text-blue-500" />;
      case 'doc': case 'docx': return <FaFileWord className="text-blue-600" />;
      case 'xls': case 'xlsx': return <FaFileExcel className="text-green-600" />;
      default: return <FaFile className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      alert('File size must be less than 10MB');
      return;
    }

    // Add file to attached files list with empty category (to be filled by user)
    const newFile = {
      id: Date.now() + Math.random(), // Simple unique ID
      file: file,
      category: '', // Empty initially - user will fill this in
      name: file.name,
      size: file.size
    };

    setAttachedFiles(prev => [...prev, newFile]);
    
    // Clear the input
    event.target.value = '';
  };

  const updateFileCategory = (fileId, category) => {
    setAttachedFiles(prev => 
      prev.map(file => 
        file.id === fileId ? { ...file, category: category.trim() } : file
      )
    );
  };

  // Check if files have changed from initial state
  const hasFileChanges = () => {
    return uploadedFiles.length !== initialUploadedFilesCount;
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleViewFile = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const signedUrl = await getSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening file:', err);
    }
  };

  const removeUploadedFile = (fileId) => {
    const fileToDelete = uploadedFiles.find(f => f.id === fileId);
    if (fileToDelete) {
      setDeleteConfirmModal({ show: true, file: fileToDelete });
    }
  };

  const confirmDeleteFile = async () => {
    const fileToDelete = deleteConfirmModal.file;
    if (!fileToDelete) return;

    setDeleting(true);
    try {
      // Call the delete API endpoint
      await deleteFile(fileToDelete.url);
      
      // Remove the file from the uploaded files list
      setUploadedFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      
      // Close the modal
      setDeleteConfirmModal({ show: false, file: null });
      
      // Show success message
      alert('File deleted successfully!');
    } catch (error) {
      console.error('File deletion error:', error);
      alert(error.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteFile = () => {
    setDeleteConfirmModal({ show: false, file: null });
  };

  const handleUploadFiles = async () => {
    if (attachedFiles.length === 0) {
      alert('No files to upload');
      return;
    }

    console.log('attachedFiles before upload:', attachedFiles);

    // Check if all files have categories
    const filesWithoutCategory = attachedFiles.filter(file => !file.category || file.category.trim() === '');
    if (filesWithoutCategory.length > 0) {
      alert('Please enter a category for all files before uploading');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const response = await uploadFiles(attachedFiles);
      console.log('Upload successful:', response);
      
      // Move uploaded files to uploadedFiles array with URLs
      const filesWithUrls = attachedFiles.map((file, index) => ({
        ...file,
        url: response.data?.urls?.[index] || null,
        uploaded: true
      }));
      
      setUploadedFiles(prev => [...prev, ...filesWithUrls]);
      
      // Clear attached files after successful upload
      setAttachedFiles([]);
      
      // Show success message
      alert(`${response.data?.urls?.length || attachedFiles.length} file(s) uploaded successfully!`);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data) => {
    setApiError(null);
    try {
      // Prepare the payload
      const payload = {
        clientId: clientId,
        ...data
      };

      // Include attachments only if there were file changes
      if (hasFileChanges()) {
        payload.attachments = uploadedFiles.map(file => ({
          type: file.category,
          url: file.url
        }));
      }

      await editClient(clientId, payload);
      navigate('/clients', { state: { message: `${terminology.client.singular} updated successfully!` } }); // Use terminology
    } catch (error) {
      setApiError(error.message || `Failed to update ${terminology.client.singular.toLowerCase()}. Please try again.`); // Use terminology
      console.error("Update error:", error);

      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  // --- Service Pricing Handlers ---
  const handleSavePricing = async (newPricing) => {
    await editClient(clientId, { servicePricing: newPricing });
    setServicePricing(newPricing);
    setShowPricingModal(false);
  };

  const handleApplyPricing = async () => {
    setShowApplyConfirm(false);
    setApplyingPricing(true);
    setApplyResult(null);
    setApplyError(null);
    try {
      const result = await applyServicePricing(clientId);
      setApplyResult(result.data);
    } catch (err) {
      setApplyError(err.message || 'Failed to apply pricing.');
    } finally {
      setApplyingPricing(false);
    }
  };
  if (loading) {
    return <p className="text-center text-gray-500 py-10">Loading {terminology.client.singular.toLowerCase()} details...</p>; // Use terminology
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-jims-cyan-dark">Edit {terminology.client.singular}</h1> {/* Use terminology */}
        <Link to="/clients" className="text-jims-cyan-dark hover:text-jims-cyan underline">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-lg shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}

        {/* Supplier Main Details */}
        <div className = "border p-6 border-gray-300 bg-gray-50 rounded-lg space-y-6">
          <div>
            <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">{terminology.client.singular} Name *</label> {/* Use terminology */}
            <input
              id="clientName"
              type="text"
              {...register("clientName", { required: `${terminology.client.singular} Name is required.` })} /* Use terminology */
              className={`mt-1 block w-full px-3 py-2 border ${errors.clientName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.clientName && <p className="mt-1 text-xs text-red-600">{errors.clientName.message}</p>}
          </div>
          <div className="flex w-full gap-6">
            <div className="flex-1">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
              <input
                id="email"
                type="email"
                {...register("email", {
                  required: "Email is required.",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address."
                  }
                })}
                className={`mt-1 block w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div className="flex-1">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number *</label>
              <input
                id="phoneNumber"
                type="tel"
                {...register("phoneNumber", { required: "Phone Number is required." })}
                className={`mt-1 block w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
              />
              {errors.phoneNumber && <p className="mt-1 text-xs text-red-600">{errors.phoneNumber.message}</p>}
            </div>
          </div>

          {/* Case Manager Contact Details */}
          <div className="border-t pt-6 mt-6 border-gray-200">
              <p className="mb-3 block text-sm font-medium text-gray-700">Case Manager Contact Details:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label htmlFor="caseManagerFirstName" className="block text-xs font-medium text-gray-700">First Name</label>
                      <input
                          id="caseManagerFirstName"
                          type="text"
                          {...register("caseManagerFirstName")}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                      />
                  </div>
                  <div>
                      <label htmlFor="caseManagerLastName" className="block text-xs font-medium text-gray-700">Last Name</label>
                      <input
                          id="caseManagerLastName"
                          type="text"
                          {...register("caseManagerLastName")}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                      />
                  </div>
              </div>
              <div className="mt-6">
                  <label htmlFor="caseManagerEmail" className="block text-xs font-medium text-gray-700">Email</label>
                  <input
                      id="caseManagerEmail"
                      type="email"
                      {...register("caseManagerEmail", {
                          pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address."
                          }
                      })}
                      className={`mt-1 block w-full px-3 py-2 border ${errors.caseManagerEmail ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                  />
                  {errors.caseManagerEmail && <p className="mt-1 text-xs text-red-600">{errors.caseManagerEmail.message}</p>}
              </div>
          </div>
        </div>

        {/* PO Number, Aged Care and Hourly Rate Fields */}
        <div className="space-y-6">
          <div>
            <label htmlFor="poNumber" className="block text-sm font-medium text-gray-700">PO Number</label>
            <input
              id="poNumber"
              type="text"
              {...register("poNumber")}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
            />
          </div>

          {/* Effective-dated PO replacement: current PO Number above stays in effect until poEffectiveDate */}
          <div className="border border-gray-200 bg-white rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Update PO Number (effective-dated)</p>
            <p className="text-xs text-gray-500 mb-3">
              The current PO Number above keeps applying to services completed before the date below.
              Enter a new PO number and a "Services completed from" date to switch — jobs completed on or after that date will use the new PO; jobs completed before it keep the current PO.
            </p>
            <div className="flex w-full gap-6">
              <div className="flex-1">
                <label htmlFor="newPoNumber" className="block text-sm font-medium text-gray-700">New PO Number</label>
                <input
                  id="newPoNumber"
                  type="text"
                  {...register("newPoNumber", {
                    validate: (value) => {
                      const effectiveDate = getValues('poEffectiveDate');
                      if (effectiveDate && effectiveDate.trim() !== '' && (!value || value.trim() === '')) {
                        return 'New PO Number is required when a "Services completed from" date is set.';
                      }
                      return true;
                    }
                  })}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.newPoNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.newPoNumber && <p className="mt-1 text-xs text-red-600">{errors.newPoNumber.message}</p>}
              </div>
              <div className="flex-1">
                <label htmlFor="poEffectiveDate" className="block text-sm font-medium text-gray-700">Services completed from</label>
                <input
                  id="poEffectiveDate"
                  type="date"
                  {...register("poEffectiveDate", {
                    validate: (value) => {
                      const newPo = getValues('newPoNumber');
                      if (newPo && newPo.trim() !== '' && (!value || value.trim() === '')) {
                        return 'Date is required when a New PO Number is set.';
                      }
                      return true;
                    }
                  })}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.poEffectiveDate ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                {errors.poEffectiveDate && <p className="mt-1 text-xs text-red-600">{errors.poEffectiveDate.message}</p>}
              </div>
            </div>
          </div>

          <div class="flex w-full gap-6">
            <div className="flex-1">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  id="agedCare"
                  type="checkbox"
                  {...register("agedCare")}
                  className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Aged Care</span>
              </label>
            </div>
            <div className="flex-1">
              <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700">Hourly Rate</label>
              <input
                id="hourlyRate"
                type="number"
                step="0.01"
                min="0"
                {...register("hourlyRate")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                placeholder="0.00"
              />
            </div>
            <div className="flex-1">
          </div>
            <label className="flex items-center space-x-2 cursor-pointer mt-6">
              <input
                id="durationInIncrements"
                type="checkbox"
                {...register("durationInIncrements")}
                className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Duration in 15 min increments</span>
            </label>
          </div>
        </div>

        {/* Contact Person Name Fields */}
        <div className="pt-6 mt-6 ">
            <p className="text-sm text-gray-600 mb-3">Contact Person Details:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name *</label>
                    <input
                        id="firstName"
                        type="text"
                        {...register("firstName", { required: "First Name is required." })}
                        className={`mt-1 block w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                    />
                    {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>}
                </div>
                <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                        id="lastName"
                        type="text"
                        {...register("lastName")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
            </div>
        </div>

        {/* Xero Contact ID Field - ADDED */}
        <div>
            <label htmlFor="xeroContactId" className="block text-sm font-medium text-gray-700">Xero Contact ID</label>
            <input
                id="xeroContactId"
                type="text"
                {...register("xeroContactId")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
            />
        </div>

        {/* Optional Fields */}
        <div className="border-t pt-6 mt-6 border-gray-200">
            <p className="text-sm text-gray-600 mb-3">Optional Address Information:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <AddressAutocomplete
                    control={control}
                    setValue={setValue}
                 />
                <div>
                    <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700">Address Line 2</label>
                    <input
                        id="addressLine2"
                        type="text"
                        {...register("addressLine2")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">State</label>
                    <input
                        id="state"
                        type="text"
                        {...register("state")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                        id="postalCode"
                        type="text"
                        {...register("postalCode")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                {/* Add Country Field */}
                <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                    <input
                        id="country"
                        type="text"
                        {...register("country")}
                        readOnly
                        value="Australia" // Explicitly set the value here
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm bg-gray-100 cursor-not-allowed"
                    />
                </div>
            </div>
        </div>

        {/* Service Pricing Section */}
        <div className="border-t pt-6 mt-6 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-gray-800">Service Pricing</p>
              <p className="text-xs text-gray-500 mt-0.5">Set FSE rate and invoice price per service type</p>
            </div>
            <button
              type="button"
              onClick={() => setShowPricingModal(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-jims-cyan text-jims-cyan text-sm rounded hover:bg-jims-cyan-light transition-colors"
            >
              <FaTag size={12} />
              Edit Pricing
            </button>
          </div>

          {/* Pricing summary table */}
          {Object.keys(servicePricing).length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-2.5 text-left">Service Type</th>
                    <th className="px-4 py-2.5 text-right">FSE Rate</th>
                    <th className="px-4 py-2.5 text-right">Invoice ex. GST</th>
                    <th className="px-4 py-2.5 text-right">Invoice w/ GST</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(servicePricing).map(([id, p]) => (
                    <tr key={id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{p.name}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">
                        {p.pricePerHour !== null && p.pricePerHour !== undefined ? `$${p.pricePerHour.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-700">
                        {p.additionalPrice !== null && p.additionalPrice !== undefined ? `$${p.additionalPrice.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-800">
                        {p.invoicePricePerHour !== null && p.invoicePricePerHour !== undefined ? `$${p.invoicePricePerHour.toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic mb-4">No service pricing configured yet. Click "Edit Pricing" to set prices.</p>
          )}

          {/* Apply to Job Schedules */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-amber-900">Apply Pricing to Job Schedules</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Updates all active job schedules (and their pending jobs) with the prices above.
                  Schedules with multiple service types will be skipped and reported.
                </p>
              </div>
              <button
                type="button"
                disabled={Object.keys(servicePricing).length === 0 || applyingPricing}
                onClick={() => { setApplyResult(null); setApplyError(null); setShowApplyConfirm(true); }}
                className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaSync size={12} className={applyingPricing ? 'animate-spin' : ''} />
                {applyingPricing ? 'Applying…' : 'Apply to Schedules'}
              </button>
            </div>

            {/* Apply result */}
            {applyResult && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-sm font-medium text-green-700">
                  ✅ {applyResult.schedulesUpdated} schedule(s) and {applyResult.jobsUpdated} job(s) updated.
                </p>
                {applyResult.skipped && applyResult.skipped.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-amber-800 mb-1">
                      ⚠️ {applyResult.skipped.length} schedule(s) skipped — action required:
                    </p>
                    <ul className="space-y-1">
                      {applyResult.skipped.map(s => (
                        <li key={s.scheduleId} className="text-xs text-amber-700">
                          <span className="font-mono text-amber-900">{s.scheduleId}</span> — {s.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {applyError && (
              <p className="mt-2 text-sm text-red-600">{applyError}</p>
            )}
          </div>
        </div>

        {/* File Upload Section */}
        <div className="border-t pt-6 mt-6 border-gray-200">
          <p className="text-sm text-gray-600 mb-3">File Attachments:</p>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="*/*"
          />

          {/* Attach File Link */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleAttachFile}
              className="inline-flex items-center text-jims-cyan hover:text-jims-cyan-dark font-medium text-sm"
            >
              <FaPaperclip className="mr-2" />
              Attach File
            </button>
            <span className="ml-2 text-xs text-gray-500">(Max 10MB per file)</span>
          </div>

          {/* Display attached files (to be uploaded) */}
          {attachedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Files to Upload:</h4>
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Category *
                    </label>
                    <input
                      type="text"
                      value={file.category}
                      onChange={(e) => updateFileCategory(file.id, e.target.value)}
                      placeholder="e.g., contracts, documents, images"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                    />
                  </div>
                </div>
              ))}
              
              {/* Upload Files Button - moved here to be right after files to upload */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleUploadFiles}
                  disabled={uploading}
                  className="inline-flex items-center px-4 py-2 bg-jims-cyan text-white text-sm font-medium rounded-md hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFileUpload className="mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${attachedFiles.length} File(s)`}
                </button>
              </div>
            </div>
          )}

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
              {uploadedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleViewFile(file.url)}
                          className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark underline truncate block text-left"
                          title="Click to download file"
                        >
                          {file.name}
                        </button>
                        <p className="text-xs text-gray-500">
                          {file.size > 0 ? formatFileSize(file.size) : 'Size unknown'} • Category: {file.category}
                        </p>
                        <p className="text-xs text-green-600 font-medium">
                          {file.existing ? '✓ Existing file' : '✓ Uploaded successfully'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md">
              {uploadError}
            </div>
          )}
        </div>

        {/* Confirmation Modal for File Deletion */}
        {deleteConfirmModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm File Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirmModal.file?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
          <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
            <Link to="/clients" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting || (!isDirty && !hasFileChanges())} // Enable if form changed OR files changed
              className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : `Save Changes`}
            </button>
          </div>
        </div>
      </form>

      {!loading && (
        <ActivityLog
          entityType="client"
          entityId={clientId}
          createdAt={createdAt}
          createdLabel={`${terminology.client.singular} created`}
        />
      )}

      {/* Service Pricing Modal */}
      {showPricingModal && (
        <ServicePricingModal
          clientName={clientName}
          initialPricing={servicePricing}
          onSave={handleSavePricing}
          onClose={() => setShowPricingModal(false)}
        />
      )}

      {/* Apply Pricing Confirmation Dialog */}
      {showApplyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Apply Pricing to Job Schedules</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will overwrite prices on <strong>all active job schedules</strong> for <strong>{clientName}</strong> based on the configured service pricing, and cascade to individual non-completed/non-invoiced jobs.
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-5">
              Schedules with multiple service types will be skipped. You can update those manually.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowApplyConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyPricing}
                className="px-5 py-2 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-700"
              >
                Yes, Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditClientPage;
